/**
 * Generic log viewer mainly targeted for use in internal tools.
 */
import type { MutableRefObject } from "react";
import { useRef, useState } from "react";
import IconClearHistory from "ui/icons/IconClearHistory";
import styles from "./DebugLogViewer.module.scss";

enum LogMessageType {
  ERROR,
  INFO,
}

export interface DebugLogMessage {
  type: LogMessageType;
  message: string;
}

export function useDebugLogViewer() {
  const logMessages = useRef<DebugLogMessage[]>([]);
  const [logMessagesGeneration, setLogMessagesGeneration] = useState<number>(0); // Used to trigger a re-render
  const logElementRef = useRef<HTMLDivElement | null>(null);

  function refreshLog() {
    setTimeout(() => {
      // This needs to happen after React re-renders
      if (logElementRef.current) {
        logElementRef.current.scrollTop = logElementRef.current.scrollHeight;
      }
    }, 0);
    setLogMessagesGeneration(logMessagesGeneration + 1);
  }

  function addErrorMessage(msg: string) {
    logMessages.current.push({ type: LogMessageType.ERROR, message: msg });
    refreshLog();
  }

  function addLogMessage(msg: string) {
    logMessages.current.push({ type: LogMessageType.INFO, message: msg });
    refreshLog();
  }

  return {
    addErrorMessage,
    addLogMessage,
    logElementRef,
    logMessages,
  };
}

interface DebugLogViewerProps {
  logMessages: MutableRefObject<DebugLogMessage[]>;
  logElementRef: MutableRefObject<HTMLDivElement | null>;
}

export default function DebugLogViewer({ logMessages, logElementRef }: DebugLogViewerProps) {
  const [gen, setGen] = useState<number>(0);

  return (
    <div className={styles.container}>
      <button
        className={styles.clearButton}
        onClick={() => {
          logMessages.current = [];
          setGen(gen + 1); // Trigger a re-render
        }}
        title="Clear log"
        type="button"
      >
        <IconClearHistory />
      </button>
      <div className={styles.wrapper} ref={logElementRef}>
        {logMessages.current.map((msg, i) => (
          <p key={i} className={msg.type === LogMessageType.ERROR ? styles.error : ""}>
            {msg.message}
          </p>
        ))}
      </div>
    </div>
  );
}
